import React, { useEffect, useId, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, CheckCircle } from 'lucide-react';

export interface ParsedAddress {
  fullAddress: string;
  street: string;
  city: string;
  province: string;
  zip: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string;
  companyName?: string;
  website?: string;
  phone?: string;
  placeTypes: string[];
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect?: (parsed: ParsedAddress) => void;
  /** Ricerca Places condivisa per indirizzi, aziende, città, Paesi e luoghi. */
  mode?: 'establishment' | 'address' | 'city' | 'country' | 'place';
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
  maxLength?: number;
  /** Filtro paese opzionale. Se omesso, Places cerca a livello globale. */
  countryRestriction?: string | string[];
}

interface AutocompleteSuggestion {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

interface NormalizedPlace {
  placeId: string;
  name?: string;
  website?: string;
  phone?: string;
  formattedAddress: string;
  latitude: number;
  longitude: number;
  types: string[];
  components: Record<string, string>;
  shortComponents: Record<string, string>;
}

export function preferredPlacePhone(place: {
  international_phone_number?: string | null;
  formatted_phone_number?: string | null;
}): string {
  return place.international_phone_number?.trim()
    || place.formatted_phone_number?.trim()
    || '';
}

function parsePlace(place: NormalizedPlace): ParsedAddress {
  const streetNumber = place.components.street_number || '';
  const route = place.components.route || '';
  const street = route ? `${route}${streetNumber ? ', ' + streetNumber : ''}` : '';

  return {
    fullAddress: place.formattedAddress || '',
    street,
    city:
      place.components.locality
      || place.components.administrative_area_level_3
      || place.components.administrative_area_level_2
      || '',
    province:
      place.shortComponents.administrative_area_level_2
      || place.shortComponents.administrative_area_level_1
      || '',
    zip: place.components.postal_code || '',
    country: place.components.country || '',
    latitude: Number.isFinite(place.latitude) ? place.latitude : null,
    longitude: Number.isFinite(place.longitude) ? place.longitude : null,
    placeId: place.placeId || '',
    companyName: place.name || '',
    website: place.website || '',
    phone: place.phone || '',
    placeTypes: place.types || [],
  };
}

function countryQuery(countryRestriction?: string | string[]): string[] {
  if (!countryRestriction) return [];
  return Array.isArray(countryRestriction) ? countryRestriction : [countryRestriction];
}

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  mode = 'establishment',
  placeholder,
  className = '',
  id,
  disabled = false,
  maxLength,
  countryRestriction,
}: AddressAutocompleteProps) {
  const generatedId = useId();
  const suggestionsId = `${id ?? generatedId}-suggestions`;
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [mapsUnavailable, setMapsUnavailable] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selected, setSelected] = useState(false);
  const [editedByUser, setEditedByUser] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const sessionTokenRef = useRef<string | null>(null);

  const defaultPlaceholder =
    mode === 'establishment' ? "Digita il nome dell'azienda..."
      : mode === 'city' ? 'Cerca una città...'
        : mode === 'country' ? 'Cerca un Paese...'
          : mode === 'place' ? 'Cerca un luogo...'
            : 'Digita un indirizzo...';

  useEffect(() => {
    const query = value.trim();
    if (query.length < 3 || selected || !editedByUser) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const params = new URLSearchParams({ q: query, mode });
        if (sessionTokenRef.current) params.set('sessionToken', sessionTokenRef.current);
        for (const country of countryQuery(countryRestriction)) {
          params.append('country', country);
        }
        const response = await fetch(`/api/google/maps/autocomplete?${params.toString()}`, {
          credentials: 'include',
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          setSuggestions([]);
          setMapsUnavailable(true);
          setErrorMessage(typeof payload.error === 'string' ? payload.error : 'Autocomplete non disponibile');
          return;
        }
        setMapsUnavailable(false);
        const nextSuggestions = Array.isArray(payload.suggestions) ? payload.suggestions : [];
        const inputRect = inputRef.current?.getBoundingClientRect();
        if (inputRect && nextSuggestions.length > 0) {
          const bottomNavigation = window.innerWidth < 640 ? 72 : 0;
          const roomBelow = window.innerHeight - inputRect.bottom - bottomNavigation;
          setOpenAbove(roomBelow < 250 && inputRect.top > roomBelow);
        }
        setSuggestions(nextSuggestions);
      } catch (error) {
        if ((error as Error)?.name !== 'AbortError') {
          setSuggestions([]);
          setMapsUnavailable(true);
          setErrorMessage('Google Maps non disponibile');
        }
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [value, mode, countryRestriction, selected, editedByUser]);

  useEffect(() => () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
  }, []);

  const selectSuggestion = async (suggestion: AutocompleteSuggestion) => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams({ placeId: suggestion.placeId });
      if (sessionTokenRef.current) params.set('sessionToken', sessionTokenRef.current);
      const response = await fetch(`/api/google/maps/details?${params.toString()}`, {
        credentials: 'include',
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.result) {
        throw new Error(typeof payload.error === 'string' ? payload.error : 'Dettaglio luogo non disponibile');
      }

      const parsed = parsePlace(payload.result as NormalizedPlace);
      if (mode === 'country' && (!parsed.placeTypes.includes('country') || !parsed.country)) {
        setErrorMessage('Seleziona un Paese, non una regione o una città');
        return;
      }
      if (mode === 'city' && !parsed.city) {
        setErrorMessage('La città selezionata non ha un nome verificabile');
        return;
      }
      const displayValue = mode === 'establishment'
        ? parsed.companyName || parsed.fullAddress || suggestion.description
        : mode === 'city' ? parsed.city || suggestion.mainText
          : mode === 'country' ? parsed.country || suggestion.mainText
            : parsed.fullAddress || suggestion.description;
      onChange(displayValue);
      setSelected(true);
      setEditedByUser(false);
      sessionTokenRef.current = null;
      setSuggestions([]);
      onAddressSelect?.(parsed);
    } catch (error) {
      setMapsUnavailable(true);
      setErrorMessage(error instanceof Error ? error.message : 'Dettaglio luogo non disponibile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!sessionTokenRef.current) sessionTokenRef.current = crypto.randomUUID();
    setSuggestions([]);
    setEditedByUser(true);
    setSelected(false);
    setErrorMessage(null);
    onChange(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // L'utente conferma un suggerimento con il click; Enter non deve propagare
    // il submit al form proprietario (azienda, filiale, ecc.).
    if (event.key === 'Enter') event.preventDefault();
  };

  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
        {selected ? (
          <CheckCircle className="h-4 w-4 text-green-500" />
        ) : (
          <MapPin className="h-4 w-4 text-blue-400" />
        )}
      </div>
      <Input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
        }}
        onBlur={() => {
          // Delay session closure so a tap on a suggestion can still send the
          // same token to Place Details on mobile as well as desktop.
          blurTimerRef.current = setTimeout(() => {
            setSuggestions([]);
            sessionTokenRef.current = null;
          }, 150);
        }}
        placeholder={placeholder ?? defaultPlaceholder}
        className={`pl-10 ${className}`}
        autoComplete="off"
        disabled={disabled}
        maxLength={maxLength}
        aria-autocomplete="list"
        aria-controls={suggestions.length ? suggestionsId : undefined}
      />
      {suggestions.length > 0 && (
        <div
          id={suggestionsId}
          role="listbox"
          className={`absolute z-50 w-full border border-border bg-background shadow-md ${openAbove ? 'bottom-full mb-1' : 'mt-1'}`}
        >
          <div className="max-h-48 overflow-y-auto sm:max-h-56">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.placeId}
              type="button"
              role="option"
              className="block w-full border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-muted"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => void selectSuggestion(suggestion)}
            >
              <span className="block text-sm text-foreground">{suggestion.mainText}</span>
              {suggestion.secondaryText && (
                <span className="block text-xs text-muted-foreground">{suggestion.secondaryText}</span>
              )}
            </button>
          ))}
          </div>
          <div className="px-3 py-1 text-right font-sans text-xs font-normal text-[#5e5e5e]" translate="no" aria-label="Google Maps">Google Maps</div>
        </div>
      )}
      {loading && <p className="text-xs text-muted-foreground mt-1">Ricerca in corso...</p>}
      {mapsUnavailable && !loading && (
        <p className="text-xs text-muted-foreground mt-1">
          {errorMessage || 'Google Maps non configurato'} — inserimento manuale attivo
        </p>
      )}
    </div>
  );
}
